import { useState } from "react";
import { motion } from "framer-motion";
import { Upload, Loader2, ShieldCheck, ArrowRight, Check,
         AlertCircle, Sparkles, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

const CATEGORIES = [
  "cement", "steel", "aggregate", "sand", "chemicals",
  "bricks", "plywood", "electricals", "plumbing", "other",
];

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

/** 5-field Executive Wallet Induction form. Mandatory Bank Mandate PDF
 * + ₹500 minimum-balance covenant checkbox. Obsidian/gold theme. */
export function WalletInductionForm({ onSubmitted, initialStatus }) {
  const { api } = useAuth();
  const [f, setF] = useState({
    monthly_trade_volume: "",
    annual_turnover: "",
    primary_category: "cement",
    bank_reference: "",
    intention: "",
  });
  const [mandate, setMandate] = useState(null);
  const [committed, setCommitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!committed) { setErr("Please commit to the ₹500 minimum-balance covenant."); return; }
    if (!mandate) { setErr("Please upload the signed Bank Mandate PDF."); return; }
    if (mandate.size > 5 * 1024 * 1024) { setErr("Mandate must be 5 MB or smaller."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, String(v ?? "")));
      fd.append("minimum_balance_committed", "true");
      fd.append("bank_mandate", mandate);
      // Explicit multipart Content-Type stops any parent axios default
      // (e.g. `application/json`) from stomping the boundary header and
      // corrupting the upload — that used to leave the request stuck at
      // the ingress and give the buyer a blank/failing form.
      const { data } = await api.post("/wallet/induction/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Application submitted · awaiting authorization");
      try { if (onSubmitted) onSubmitted(data); }
      catch (cbEx) { console.error("[wallet-induction] onSubmitted callback error:", cbEx); }
    } catch (ex) {
      // Defensive: FastAPI validation errors return `detail` as an
      // array of objects. If we set that raw into React state the
      // JSX render below would throw "Objects are not valid as a
      // React child" and blank the entire page. Coerce everything
      // through `formatApiErrorDetail` so `err` is always a string.
      console.error("[wallet-induction] submit failed:", ex,
                      "response:", ex?.response?.data);
      const detail = ex?.response?.data?.detail;
      const msg = detail !== undefined
        ? formatApiErrorDetail(detail)
        : (ex?.message === "Network Error"
            ? "Network dropped mid-upload. Please check your connection and try again — the mandate file might be too large or the connection unstable."
            : "Submission failed. Please try again in a moment.");
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      onSubmit={submit}
      data-testid="wallet-induction-form"
      className="max-w-3xl mx-auto space-y-8 mt-10"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-[#8A6A1A] font-grotesk text-[10px] uppercase tracking-[0.4em] mb-3">
          <Sparkles className="w-3 h-3" /> Executive Wallet · Induction Application
        </div>
        <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] leading-tight">
          Apply for Executive
          <span className="block bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] bg-clip-text text-transparent">
            Wallet Access.
          </span>
        </h2>
        <p className="mt-6 text-[#C9C9C2] max-w-xl mx-auto text-sm leading-relaxed">
          The Vinci Executive Wallet is granted only to serious counterparties.
          Complete the scrutiny below — an admin will personally review your
          application within 48 hours.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Expected monthly trade volume (₹) *"
                 testid="wi-volume"
                 type="number" min="10000" step="1000"
                 value={f.monthly_trade_volume}
                 onChange={set("monthly_trade_volume")}
                 hint={f.monthly_trade_volume ? inr(f.monthly_trade_volume) : "Min ₹10,000"} />
        <Field label="Annual business turnover (₹) *"
                 testid="wi-turnover"
                 type="number" min="0" step="10000"
                 value={f.annual_turnover}
                 onChange={set("annual_turnover")}
                 hint={f.annual_turnover ? inr(f.annual_turnover) : "Approx last FY"} />
      </div>

      <SelectField label="Primary trading category *"
                     testid="wi-category"
                     value={f.primary_category}
                     onChange={set("primary_category")}
                     options={CATEGORIES} />

      <Field label="Bank reference (bank name · branch · IFSC · AC last 4) *"
               testid="wi-bank"
               value={f.bank_reference}
               onChange={set("bank_reference")}
               placeholder="e.g. HDFC Bank · Andheri West · HDFC0000123 · AC ****1122" />

      <div>
        <label className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A6A1A] block mb-2">
          Intended use of the Vinci Executive Wallet *
        </label>
        <textarea
          data-testid="wi-intention"
          value={f.intention}
          onChange={set("intention")}
          rows={4}
          placeholder="How will your business use this wallet? Trading focus, expected settlement pace, growth thesis, etc. (min 20 chars)"
          className="w-full bg-transparent border border-white/10 focus:border-[#B87333] outline-none font-body text-sm px-4 py-3 transition-colors resize-none"
        />
        <div className="text-[10px] text-[#8A8A82] mt-1 tracking-widest uppercase">
          {(f.intention || "").length} / 800
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <label className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A6A1A]">
            Bank Mandate · signed PDF *
          </label>
          <BankMandateTemplateLink api={api} />
        </div>
        <label htmlFor="wi-mandate"
                data-testid="wi-mandate-drop"
                className={`flex items-center gap-4 border-2 border-dashed ${mandate ? "border-[#B87333]/60 bg-[#B87333]/5" : "border-white/10 hover:border-[#B87333]/40"} px-5 py-4 cursor-pointer transition-colors`}>
          <Upload className="w-5 h-5 text-[#B87333]" />
          <div className="flex-1 min-w-0">
            {mandate ? (
              <>
                <div className="font-body text-sm text-[#F5F5F0] truncate">{mandate.name}</div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] mt-1">
                  {(mandate.size / 1024).toFixed(0)} KB · click to replace
                </div>
              </>
            ) : (
              <>
                <div className="font-body text-sm text-[#F5F5F0]">Attach the signed Bank Mandate</div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] mt-1">
                  PDF · up to 5 MB · directly to your primary bank
                </div>
              </>
            )}
          </div>
        </label>
        <input id="wi-mandate" data-testid="wi-mandate-input"
                 type="file" accept=".pdf"
                 onChange={(e) => setMandate(e.target.files?.[0] || null)}
                 className="hidden" />
      </div>

      <label htmlFor="wi-commit"
              className="flex items-start gap-3 border border-[#D8B23E]/30 bg-[#D8B23E]/5 px-5 py-4 cursor-pointer">
        <input
          id="wi-commit"
          data-testid="wi-commit-checkbox"
          type="checkbox"
          checked={committed}
          onChange={(e) => setCommitted(e.target.checked)}
          className="mt-1 accent-[#D8B23E] w-4 h-4"
        />
        <div className="flex-1">
          <div className="font-serif text-base text-[#F6E4A6]">
            I commit to maintaining the ₹500 minimum wallet balance covenant.
          </div>
          <div className="text-xs text-[#C9C9C2] mt-1 leading-relaxed">
            Falling below ₹500 will pause my trading access until the floor is restored.
            I understand the Vinci Wallet is a closed-loop ledger — not a bank deposit and not covered by DICGC.
          </div>
        </div>
      </label>

      {err && (
        <div className="flex items-start gap-2 text-[#F8B5A8] font-body text-sm border border-[#C0392B]/40 bg-[#C0392B]/10 px-4 py-3 rounded-sm"
             data-testid="wi-error" role="alert">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="flex-1">{typeof err === "string" ? err : "Submission failed."}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={busy}
        data-testid="wi-submit"
        className="w-full py-4 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk text-xs uppercase tracking-[0.32em] font-medium hover:shadow-[0_0_40px_rgba(216,178,62,0.35)] transition-shadow disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3"
      >
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Submitting</> : <>Submit Application for Executive Access<ArrowRight className="w-4 h-4" /></>}
      </button>
      <p className="text-center font-body text-[10px] text-[#6f6f68] leading-relaxed">
        Your mandate is stored in the Vinci Legal Vault and reviewed only by our finance desk.
        We never share it with third parties.
      </p>
    </motion.form>
  );
}


function BankMandateTemplateLink({ api }) {
  const [busy, setBusy] = useState(false);
  const grab = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.get("/wallet/induction/bank-mandate-template", {
        responseType: "blob",
      });
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const filename = m ? m[1] : "vinci-bank-mandate.pdf";
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Bank Mandate template downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch template");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={grab}
      disabled={busy}
      data-testid="wi-mandate-template"
      title="Download the pre-filled Vinci Bank Mandate PDF — sign, stamp, and re-upload."
      className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#D8B23E] hover:text-[#F6E4A6] transition-colors border-b border-dotted border-[#D8B23E]/40 hover:border-[#F6E4A6] pb-0.5"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
      Download Template
    </button>
  );
}


function Field({ label, testid, hint, ...rest }) {
  return (
    <div>
      <label className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A6A1A] block mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        {...rest}
        className="w-full bg-transparent border border-white/10 focus:border-[#B87333] outline-none font-body text-sm px-4 py-3 transition-colors"
      />
      {hint && <div className="text-[10px] text-[#8A8A82] mt-1 tracking-widest uppercase">{hint}</div>}
    </div>
  );
}
function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A6A1A] block mb-2">
        {label}
      </label>
      <select data-testid={testid} value={value} onChange={onChange}
               className="w-full bg-transparent border border-white/10 focus:border-[#B87333] outline-none font-body text-sm px-4 py-3 transition-colors">
        {options.map(o => <option key={o} value={o} className="bg-black">{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
      </select>
    </div>
  );
}


/** Post-submission pending screen shown to applicants awaiting review. */
export function InductionPendingScreen({ application, onAcknowledge }) {
  const [busy, setBusy] = useState(false);
  const isDecisionReady = application?.status === "approved"
    || application?.status === "rejected";
  const alreadyAck = !!application?.acknowledged;
  const approved = application?.status === "approved";
  const conditions = approved
    ? (application?.executive_conditions || "")
    : (application?.rejection_reason || "");
  const conditionsList = (conditions || "")
    .replace(/;/g, "\n").split("\n")
    .map((s) => s.trim().replace(/^[•\-*·]\s*/, ""))
    .filter(Boolean);

  const acknowledge = async () => {
    if (!onAcknowledge) return;
    setBusy(true);
    try { await onAcknowledge(); }
    finally { setBusy(false); }
  };

  if (isDecisionReady && !alreadyAck) {
    const accent = approved ? "#D8B23E" : "#C0654E";
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto py-14"
        data-testid="wi-decision-ready"
      >
        <div className="text-center mb-8">
          <div className="w-24 h-24 mx-auto rounded-full grid place-items-center mb-6"
                style={{
                  border: `1px solid ${accent}`,
                  boxShadow: `0 0 80px rgba(216,178,62,${approved ? 0.45 : 0.25})`,
                }}>
            <ShieldCheck className="w-9 h-9" style={{ color: accent }} />
          </div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] mb-3"
                style={{ color: accent }}>
            Board Resolution · Decision Ready
          </div>
          <h2 className="font-serif text-4xl text-[#F5F5F0]">
            {approved
              ? (conditionsList.length
                  ? "You have been onboarded — conditionally."
                  : "You have been onboarded.")
              : "Your application was not cleared this round."}
          </h2>
          <p className="mt-5 text-[#C9C9C2] font-body max-w-xl mx-auto">
            {approved
              ? "The Vinci Board of Trade has authorised your Executive Wallet. Review the executive conditions below, then acknowledge to receive your official Board Resolution PDF on Vinci letterhead."
              : "The Board has recorded its decision. Review the reasoning below and acknowledge to receive the official Board Resolution PDF."}
          </p>
        </div>

        {conditionsList.length > 0 && (
          <div className="border rounded-sm overflow-hidden mb-8"
                style={{ borderColor: `${accent}40`,
                          background: "linear-gradient(180deg, rgba(216,178,62,0.06), rgba(0,0,0,0.35))",
                          backdropFilter: "blur(10px)" }}
                data-testid="wi-decision-conditions">
            <div className="px-5 py-3 border-b font-grotesk text-[9px] uppercase tracking-[0.32em]"
                  style={{ borderColor: `${accent}30`, color: accent }}>
              {approved ? "Executive Conditions" : "Reasons for Non-Clearance"}
            </div>
            <ul className="px-6 py-4 space-y-2">
              {conditionsList.map((c, i) => (
                <li key={i} className="text-[#F5F5F0] font-body text-sm flex gap-2">
                  <span style={{ color: accent }}>◆</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-center">
          <button
            onClick={acknowledge}
            disabled={busy}
            data-testid="wi-decision-acknowledge"
            className="group inline-flex items-center gap-3 px-10 py-4 rounded-sm font-grotesk text-[11px] uppercase tracking-[0.4em] transition-transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(90deg, ${accent}, #F6E4A6, #B87333)`,
              color: "#0A0906",
              boxShadow: `0 20px 60px -20px ${accent}, inset 0 0 20px rgba(255,255,255,0.15)`,
            }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Acknowledge &amp; accept
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="mt-4 text-[10px] font-grotesk uppercase tracking-[0.32em] text-[#8A8A82]">
            One click · your Board Resolution PDF will arrive by email in seconds
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      className="max-w-2xl mx-auto text-center py-16"
      data-testid="wi-pending-screen"
    >
      <div className="w-20 h-20 mx-auto rounded-full border border-[#D8B23E] grid place-items-center mb-6"
           style={{ boxShadow: "0 0 60px rgba(216,178,62,0.3)" }}>
        <ShieldCheck className="w-8 h-8 text-[#D8B23E]" />
      </div>
      <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#8A6A1A] mb-4">
        {alreadyAck
          ? "Induction · Acknowledged"
          : "Induction · Pending Authorization"}
      </div>
      <h2 className="font-serif text-3xl text-[#F5F5F0]">
        {alreadyAck ? "Board Resolution acknowledged." : "Under review."}
      </h2>
      <p className="mt-6 text-[#C9C9C2] text-sm leading-relaxed">
        {alreadyAck
          ? "Your official Vinci Board Resolution PDF is on file. Enter your wallet to begin trading."
          : "Your Executive Wallet application is on the Vinci Finance Desk. Our admin will personally review your Bank Mandate and financial commitment within 48 hours. You'll receive a gold-pulse activation email the moment we authorize your wallet."}
      </p>
      {application?.created_at && (
        <div className="mt-8 inline-block font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82] border border-white/10 px-4 py-2">
          Submitted {new Date(application.created_at).toLocaleString()}
        </div>
      )}
    </motion.div>
  );
}


/** Post-rejection screen — allows reapplication. */
export function InductionRejectedScreen({ application, onReapply }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto text-center py-16"
      data-testid="wi-rejected-screen"
    >
      <div className="w-20 h-20 mx-auto rounded-full border border-[#C0392B] grid place-items-center mb-6">
        <AlertCircle className="w-8 h-8 text-[#C0392B]" />
      </div>
      <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#8A6A1A] mb-4">
        Application · Not authorized
      </div>
      <h2 className="font-serif text-3xl text-[#F5F5F0]">Please revise and re-apply.</h2>
      {application?.rejection_reason && (
        <div className="mt-6 border-l-2 border-[#B87333] bg-[#0F0C05] px-5 py-4 text-left text-[#F6E4A6] font-body italic">
          "{application.rejection_reason}"
        </div>
      )}
      <button onClick={onReapply}
              data-testid="wi-reapply"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#B87333]/60 hover:bg-[#B87333] hover:text-black font-grotesk text-xs uppercase tracking-[0.28em] transition-colors">
        <Check className="w-3.5 h-3.5" /> Re-apply now
      </button>
    </motion.div>
  );
}
